import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EAutocomplete,
  EForm,
  ESmallButton,
  ETextArea,
} from 'webapp-lib/Components';

import {ActionButtons, Loading} from '#';
import handledBackend, {
  TaskDueDateReachedNotification,
  TaskResponse,
} from '@lexian/api/notifications-api';
import {Task} from '_/assets/images';
import {useRouteNavigate} from '_/hooks/useRouteNavigate';
import {I18NextNS} from '_/i18n';
import {State} from '_/utilities';
import {EFDateInput, useForm} from '_/utilities/form';
import {Loadable} from '_/utilities/types';

import {StoreContext} from '../../store';

import {ExclamationMark, FormHeader, HeaderRow, TextWrapper} from './styles';

export type Props = {
  notification: TaskDueDateReachedNotification;
};

enum TaskDueDateReachedFormFields {
  Date = 'date',
}

const isTaskResponse = (task: unknown): task is TaskResponse =>
  Boolean((task as TaskResponse).id);

const UTaskDueDateReached = ({notification}: Props): React.ReactElement => {
  const [loadable, setLoadable] = useState<Loadable<TaskResponse>>({
    state: State.Loading,
  });

  useEffect(() => {
    handledBackend.getTask({
      parameters: {
        casusId: notification.entity.casusId,
        taskId: notification.entity.id,
      },
      onSuccess: ({body}) => {
        setLoadable(
          isTaskResponse(body)
            ? {
                state: State.Success,
                data: body,
              }
            : {state: State.Error}
        );
      },
    });
  }, [setLoadable, notification.entity]);

  return (
    <div>
      <HeaderRow>
        <Task />
        <ExclamationMark show={notification.importantFlag} />
        <TextWrapper>{notification.message}</TextWrapper>
      </HeaderRow>
      {loadable.state === State.Success ? (
        <TaskDueDateForm notification={notification} task={loadable.data!} />
      ) : (
        <Loading />
      )}
    </div>
  );
};

const TaskDueDateForm = ({
  notification,
  task,
}: {
  notification: TaskDueDateReachedNotification;
  task: TaskResponse;
}) => {
  const store = useContext(StoreContext);
  const {t} = useTranslation<I18NextNS>('notifications');
  const {goToCasusTasks} = useRouteNavigate();
  const {handleSubmit, control} = useForm({
    defaultValues: {
      [TaskDueDateReachedFormFields.Date]: task.dueDate,
    },
    onSubmit: (values) => {
      store.resolveTask({
        notificationId: notification.id,
        rescheduleDate: values[TaskDueDateReachedFormFields.Date],
      });
    },
  });

  const completeTask = () => {
    store.resolveTask({notificationId: notification.id});
  };

  return (
    <EForm onSubmit={handleSubmit}>
      <FormHeader>{t('taskDueDateReached.taskDetail')}</FormHeader>
      <ETextArea
        label={t('taskDueDateReached.description')}
        value={task.description}
        disabled
      />
      <EFDateInput
        control={control}
        label={t('taskDueDateReached.date')}
        name={TaskDueDateReachedFormFields.Date}
      />
      <EAutocomplete
        label={t('taskDueDateReached.assignee')}
        options={[
          {
            value: task.assignedUser!.id,
            displayText: task.assignedUser!.displayName,
          },
        ]}
        value={task.assignedUser!.id}
        disabled
      />
      <SplittedActionButtons>
        <ESmallButton type='submit'>
          {t('taskDueDateReached.reschedule')}
        </ESmallButton>
        <ESmallButton onClick={completeTask}>
          {t('taskDueDateReached.complete')}
        </ESmallButton>
        <ESmallButton
          onClick={() =>
            goToCasusTasks({
              casusId: notification.entity.casusId,
              taskId: notification.entity.id,
            })
          }
        >
          {t('common:actions.goToSource')}
        </ESmallButton>
      </SplittedActionButtons>
    </EForm>
  );
};

const SplittedActionButtons = styled(ActionButtons)`
  span:last-of-type {
    margin-left: auto;
  }
`;

export const TaskDueDateReached = observer(UTaskDueDateReached);
